/**
 * Brand-accurate logos for Bangladeshi e-wallets, served as official brand
 * artwork from `/payment-logos/` (the operator-supplied assets):
 *
 *   bkash.webp  — pink origami bird on transparent (bKash brand mark)
 *   nagad.png   — red rounded-square with white spiral & running figure (Nagad mark)
 *   rocket.webp — purple Rocket app icon with Bangla wordmark
 *
 * The tiles below frame each asset in a brand-coloured surface that matches
 * the e-wallet's primary colour so the logo reads instantly even at small
 * sizes. Used in:
 *   - Agent panel  → BDT Deposit (Step-1 wallet picker + history rows)
 *   - Admin panel  → BDT Deposit list / Config page (where applicable)
 */
import React from "react";

const TILE_BASE =
  "relative shrink-0 overflow-hidden flex items-center justify-center rounded-xl sm:rounded-2xl transition-all duration-200";

const META = {
  bkash: {
    src: "/payment-logos/bkash.png",
    background: "#ffffff",
    shadow: "0 14px 30px -12px rgba(226,19,110,0.55), inset 0 1px 0 rgba(255,255,255,0.7)",
    border: "1px solid rgba(226,19,110,0.18)",
    pad: "10%",
    inner: "transparent",
  },
  nagad: {
    src: "/payment-logos/nagad.png",
    background: "#ffffff",
    shadow: "0 14px 30px -12px rgba(238,44,40,0.45), inset 0 1px 0 rgba(255,255,255,0.7)",
    border: "1px solid rgba(0,0,0,0.06)",
    pad: "6%",
    inner: "transparent",
  },
  rocket: {
    src: "/payment-logos/rocket.png",
    background: "#ffffff",
    shadow: "0 14px 30px -12px rgba(142,27,143,0.55), inset 0 1px 0 rgba(255,255,255,0.7)",
    border: "1px solid rgba(142,27,143,0.18)",
    pad: "6%",
    inner: "transparent",
  },
};

function Tile({ id, size = 56 }) {
  const m = META[id];
  if (!m) {
    return (
      <div
        className={TILE_BASE}
        style={{ width: size, height: size, background: "rgba(255,255,255,0.06)" }}
      />
    );
  }
  return (
    <div
      className={TILE_BASE}
      style={{
        width: size,
        height: size,
        background: m.background,
        boxShadow: m.shadow,
        border: m.border,
      }}
      data-testid={`brand-logo-${id}`}
    >
      <img
        src={m.src}
        alt={id}
        draggable={false}
        loading="lazy"
        style={{
          width: `calc(100% - ${m.pad} * 2)`,
          height: `calc(100% - ${m.pad} * 2)`,
          objectFit: "contain",
          display: "block",
          /* Tiny lift on hover handled by parent transform */
        }}
      />
    </div>
  );
}

export function BkashLogo({ size = 56 })  { return <Tile id="bkash"  size={size} />; }
export function NagadLogo({ size = 56 })  { return <Tile id="nagad"  size={size} />; }
export function RocketLogo({ size = 56 }) { return <Tile id="rocket" size={size} />; }

/* Drop-in dispatcher */
export function WalletBrandLogo({ id, size = 56 }) {
  return <Tile id={(id || "").toLowerCase()} size={size} />;
}
